/**
 * One-click A/B of a pack feature, from a run.
 *
 * The same Project, the same persona/language/turn budget (the simulator has no
 * seed, so real-model runs are similar, not identical): the testing version as
 * it is now, and a second one that differs in exactly one key of the `features:`
 * section of `flow_config.yaml`. Built from the existing file, freeze and run APIs.
 * `flow_config.yaml` is put back as it was whether or not the variant could be frozen.
 */
import { ApiError } from "@/api/client";
import type { CandidateSummary } from "@/api/preview";

import { isCurrent } from "./candidateFreshness";
import type { LabFlags } from "./labServices";

export const FEATURES_PATH = "flow_config.yaml";

export type AbFeatureValue = boolean | string;

export type AbDeps = {
  readFeaturesFile(): Promise<{ content: string; revision: number }>;
  writeFeaturesFile(
    content: string,
    expectedRevision: number,
  ): Promise<{ revision: number }>;
  /** The newest unexpired testing version of the current configuration, if any. */
  currentCandidateId(): string | null;
  freeze(): Promise<string>;
  startRun(candidateId: string, labFlags?: LabFlags): Promise<string>;
};

export type AbFailureKind =
  | "conflict"
  | "write_failed"
  | "freeze_failed"
  | "run_failed"
  | "restore_failed";

export class AbError extends Error {
  readonly kind: AbFailureKind;
  constructor(kind: AbFailureKind, options?: { cause?: unknown }) {
    super(kind, options);
    this.kind = kind;
  }
}

export const AB_FAILURE_MESSAGES = {
  conflict:
    "flow_config.yaml was changed by someone else while you were setting this up. Nothing was frozen or started; reload and try again.",
  write_failed:
    "flow_config.yaml could not be changed for the variant. Nothing was frozen or started.",
  freeze_failed:
    "The variant could not be frozen. flow_config.yaml was put back as it was.",
  run_failed:
    "A run could not be started. The testing versions exist; start the runs from the New run form.",
  restore_failed:
    "flow_config.yaml could not be put back as it was. Open Pack files and check flow_config.yaml before doing anything else.",
} as const satisfies Record<AbFailureKind, string>;

function renderValue(value: AbFeatureValue): string {
  // Strings are always quoted: bare off/on read as booleans in YAML 1.1.
  return typeof value === "boolean" ? String(value) : JSON.stringify(value);
}

/**
 * `text` (a flow_config.yaml) with `features.<name>` set to `value`; every other
 * line is untouched. A missing `features:` section is appended.
 */
export function setFeatureLine(
  text: string,
  name: string,
  value: AbFeatureValue,
): string {
  const lines = text === "" ? [] : text.replace(/\n$/, "").split("\n");
  const head = lines.findIndex((line) => /^features[ \t]*:/.test(line));
  if (head === -1) {
    return `${text}${text === "" || text.endsWith("\n") ? "" : "\n"}features:\n  ${name}: ${renderValue(value)}\n`;
  }
  const inline = (lines[head] ?? "")
    .replace(/^features[ \t]*:/, "")
    .replace(/#.*$/, "")
    .trim();
  if (inline !== "" && inline !== "{}") {
    throw new Error(
      "features is written inline; edit flow_config.yaml by hand",
    );
  }
  lines[head] = "features:";
  let end = head + 1;
  while (end < lines.length && /^([ \t]|$)/.test(lines[end] ?? "")) {
    end += 1;
  }
  const indent =
    lines
      .slice(head + 1, end)
      .find((line) => line.trim() !== "")
      ?.match(/^[ \t]+/)?.[0] ?? "  ";
  const entry = `${indent}${name}: ${renderValue(value)}`;
  const key = new RegExp(
    `^[ \\t]+${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}[ \\t]*:`,
  );
  const at = lines.slice(head + 1, end).findIndex((line) => key.test(line));
  if (at === -1) {
    lines.splice(head + 1, 0, entry);
  } else {
    lines[head + 1 + at] = entry;
  }
  return `${lines.join("\n")}\n`;
}

/** The newest current, unexpired candidate, else null. */
export function newestCurrentCandidateId(
  items: readonly CandidateSummary[],
): string | null {
  const usable = items
    .filter((item) => isCurrent(item) && item.validity?.state !== "expired")
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  return usable[0]?.candidateId ?? null;
}

export async function runFeatureAb(
  deps: AbDeps,
  change: { name: string; value: AbFeatureValue },
  /** The source run's run options, kept on BOTH runs so only the pack feature differs. */
  labFlags?: LabFlags,
): Promise<{ baseRunRef: string; variantRunRef: string }> {
  const original = await deps.readFeaturesFile();
  let baseCandidateId = deps.currentCandidateId();
  if (baseCandidateId === null) {
    try {
      baseCandidateId = await deps.freeze();
    } catch (cause) {
      throw new AbError("freeze_failed", { cause });
    }
  }

  let written: { revision: number };
  try {
    written = await deps.writeFeaturesFile(
      setFeatureLine(original.content, change.name, change.value),
      original.revision,
    );
  } catch (cause) {
    throw new AbError(
      cause instanceof ApiError && cause.status === 409
        ? "conflict"
        : "write_failed",
      { cause },
    );
  }

  // The freeze failure is held, not thrown, so the restore below always runs.
  let variantCandidateId: string | null = null;
  let freezeFailure: unknown = null;
  try {
    variantCandidateId = await deps.freeze();
  } catch (cause) {
    freezeFailure = cause;
  }
  try {
    await deps.writeFeaturesFile(original.content, written.revision);
  } catch (cause) {
    throw new AbError("restore_failed", { cause });
  }
  if (variantCandidateId === null) {
    throw new AbError("freeze_failed", { cause: freezeFailure });
  }

  try {
    const baseRunRef = await deps.startRun(baseCandidateId, labFlags);
    const variantRunRef = await deps.startRun(variantCandidateId, labFlags);
    return { baseRunRef, variantRunRef };
  } catch (cause) {
    throw new AbError("run_failed", { cause });
  }
}

/**
 * A/B of a run option: two runs on the SAME testing version, differing only in
 * one `labFlags` value. No pack change, no freeze of a copy; a freeze only
 * happens when the project has no current testing version at all.
 */
export async function runOptionAb(
  deps: Pick<AbDeps, "currentCandidateId" | "freeze" | "startRun">,
  change: { name: string; baseValue: string; variantValue: string },
  /** The source run's run options; `change.name` is overridden on each run. */
  labFlags: LabFlags = {},
): Promise<{ baseRunRef: string; variantRunRef: string }> {
  let candidateId = deps.currentCandidateId();
  if (candidateId === null) {
    try {
      candidateId = await deps.freeze();
    } catch (cause) {
      throw new AbError("freeze_failed", { cause });
    }
  }
  try {
    const baseRunRef = await deps.startRun(candidateId, {
      ...labFlags,
      [change.name]: change.baseValue,
    });
    const variantRunRef = await deps.startRun(candidateId, {
      ...labFlags,
      [change.name]: change.variantValue,
    });
    return { baseRunRef, variantRunRef };
  } catch (cause) {
    throw new AbError("run_failed", { cause });
  }
}
